import { useEffect, useRef, useState } from "react";
import { api } from "../services/api";

declare global {
  interface Window {
    MercadoPago: any;
  }
}

const BACKEND_URL = "/finance/mercadopago/test-payment";
const MP_PUBLIC_KEY = import.meta.env.VITE_MP_PUBLIC_KEY as string;

type Status = "idle" | "loading" | "success" | "error";

const loadMpScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (window.MercadoPago) return resolve();
    const script = document.createElement("script");
    script.src = "https://sdk.mercadopago.com/js/v2";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Falha ao carregar SDK do MP"));
    document.head.appendChild(script);
  });

export default function PagamentoCartao() {
  const mpRef = useRef<any>(null);
  const fieldsRef = useRef<{
    cardNumber: any;
    expirationDate: any;
    securityCode: any;
  } | null>(null);

  const [status, setStatus] = useState<Status>("idle");
  const [resultado, setResultado] = useState<any>(null);

  // Dados preenchidos pelo usuário (fora dos iframes)
  const [cardholderName, setCardholderName] = useState("");
  const [docNumber, setDocNumber] = useState("");
  const [payerEmail, setPayerEmail] = useState("");
  const [amount, setAmount] = useState("15.00");
  const [installments, setInstallments] = useState(1);
  const [paymentMethodId, setPaymentMethodId] = useState("");
  const [issuerId, setIssuerId] = useState("");

  // Opções dinâmicas
  const [installmentOptions, setInstallmentOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [issuerOptions, setIssuerOptions] = useState<
    { id: string; name: string }[]
  >([]);

  useEffect(() => {
    let cardNumberField: any = null;

    const init = async () => {
      await loadMpScript();
      const mp = new window.MercadoPago(MP_PUBLIC_KEY);
      mpRef.current = mp;

      // Monta iframes nos containers
      cardNumberField = mp.fields
        .create("cardNumber", { placeholder: "0000 0000 0000 0000" })
        .mount("mp-card-number");

      const expirationDateField = mp.fields
        .create("expirationDate", { placeholder: "MM/AA" })
        .mount("mp-expiration-date");

      const securityCodeField = mp.fields
        .create("securityCode", { placeholder: "CVV" })
        .mount("mp-security-code");

      fieldsRef.current = {
        cardNumber: cardNumberField,
        expirationDate: expirationDateField,
        securityCode: securityCodeField,
      };

      // Detecta bandeira quando os primeiros 8 dígitos são preenchidos
      cardNumberField.on("binChange", async ({ bin }: { bin: string }) => {
        if (!bin || bin.length < 8) {
          setPaymentMethodId("");
          setIssuerId("");
          setInstallmentOptions([]);
          setIssuerOptions([]);
          return;
        }

        try {
          const { results } = await mp.getPaymentMethods({ bin });
          const pm = results?.[0];
          if (!pm) return;

          setPaymentMethodId(pm.id);

          // Issuers
          let issuers: any[] = [pm.issuer];
          if (pm.additional_info_needed?.includes("issuer_id")) {
            issuers = await mp.getIssuers({ paymentMethodId: pm.id, bin });
          }
          setIssuerOptions(
            issuers.map((i: any) => ({ id: String(i.id), name: i.name })),
          );
          if (issuers[0]) setIssuerId(String(issuers[0].id));

          // Parcelas
          const installmentsData = await mp.getInstallments({
            amount: amount || "15",
            bin,
            paymentTypeId: "credit_card",
          });
          const payerCosts = installmentsData?.[0]?.payer_costs ?? [];
          setInstallmentOptions(
            payerCosts.map((p: any) => ({
              label: p.recommended_message,
              value: p.installments,
            })),
          );
          if (payerCosts[0]) setInstallments(payerCosts[0].installments);
        } catch (e) {
          console.error("Erro ao detectar bandeira:", e);
        }
      });
    };

    init().catch(console.error);

    return () => {
      // Cleanup dos campos ao desmontar
      try {
        fieldsRef.current?.cardNumber?.unmount?.();
        fieldsRef.current?.expirationDate?.unmount?.();
        fieldsRef.current?.securityCode?.unmount?.();
      } catch {}
    };
  }, []);

  const handleSubmit = async () => {
    const mp = mpRef.current;
    if (!mp || !fieldsRef.current) return;

    setStatus("loading");
    setResultado(null);

    try {
      // Tokeniza via iframes — o método correto para produção
      const token = await mp.fields.createCardToken({
        cardholderName,
        identificationType: "CPF",
        identificationNumber: docNumber.replace(/\D/g, ""),
      });

      if (!token?.id) {
        throw new Error("Falha ao gerar token: " + JSON.stringify(token));
      }

      const { data } = await api.post(BACKEND_URL, {
        cardToken: token.id,
        paymentMethodId,
        transactionAmount: Number(amount),
        installments,
        payerEmail,
        issuerId: issuerId || undefined,
        description: "Teste de pagamento",
      });

      setResultado(data);
      setStatus(data.status === "processed" ? "success" : "error");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ?? err?.message ?? "Erro desconhecido.";
      setResultado({ error: message });
      setStatus("error");
    }
  };

  const iframeStyle: React.CSSProperties = {
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "11px 14px",
    height: 44,
    background: "#fff",
    marginBottom: 12,
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "11px 14px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 15,
    marginBottom: 12,
    outline: "none",
    boxSizing: "border-box",
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    background: "#fff",
    cursor: "pointer",
  };

  return (
    <div
      style={{
        maxWidth: 440,
        margin: "40px auto",
        fontFamily: "'Segoe UI', sans-serif",
        padding: "0 16px",
      }}
    >
      <h2 style={{ marginBottom: 24, fontSize: 20, fontWeight: 700 }}>
        Teste de Pagamento — Cartão
      </h2>

      {/* Campos PCI via iframes */}
      <label style={labelStyle}>Número do cartão</label>
      <div id="mp-card-number" style={iframeStyle} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={labelStyle}>Validade</label>
          <div
            id="mp-expiration-date"
            style={{ ...iframeStyle, marginBottom: 0 }}
          />
        </div>
        <div>
          <label style={labelStyle}>CVV</label>
          <div
            id="mp-security-code"
            style={{ ...iframeStyle, marginBottom: 0 }}
          />
        </div>
      </div>

      <div style={{ marginBottom: 12 }} />

      {/* Campos normais */}
      <label style={labelStyle}>Nome no cartão</label>
      <input
        style={inputStyle}
        placeholder="Como está impresso"
        value={cardholderName}
        onChange={(e) => setCardholderName(e.target.value)}
      />

      <label style={labelStyle}>CPF do titular</label>
      <input
        style={inputStyle}
        placeholder="Somente números"
        value={docNumber}
        onChange={(e) => setDocNumber(e.target.value)}
        maxLength={11}
      />

      {/* Emissor — aparece após detectar bandeira */}
      {issuerOptions.length > 1 && (
        <>
          <label style={labelStyle}>Banco emissor</label>
          <select
            style={selectStyle}
            value={issuerId}
            onChange={(e) => setIssuerId(e.target.value)}
          >
            {issuerOptions.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </select>
        </>
      )}

      {/* Parcelas — aparece após detectar bandeira */}
      {installmentOptions.length > 0 && (
        <>
          <label style={labelStyle}>Parcelas</label>
          <select
            style={selectStyle}
            value={installments}
            onChange={(e) => setInstallments(Number(e.target.value))}
          >
            {installmentOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </>
      )}

      <label style={labelStyle}>E-mail do pagador</label>
      <input
        style={inputStyle}
        type="email"
        placeholder="email@exemplo.com"
        value={payerEmail}
        onChange={(e) => setPayerEmail(e.target.value)}
      />

      <label style={labelStyle}>Valor (R$)</label>
      <input
        style={inputStyle}
        type="number"
        step="0.01"
        placeholder="15.00"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />

      <button
        onClick={handleSubmit}
        disabled={status === "loading"}
        style={{
          width: "100%",
          padding: "13px",
          background: status === "loading" ? "#9ca3af" : "#009ee3",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          fontSize: 16,
          fontWeight: 600,
          cursor: status === "loading" ? "not-allowed" : "pointer",
          marginTop: 4,
        }}
      >
        {status === "loading" ? "Processando..." : "Pagar"}
      </button>

      {resultado && (
        <pre
          style={{
            marginTop: 20,
            padding: 14,
            borderRadius: 8,
            background:
              status === "success"
                ? "#e6f9ee"
                : status === "error"
                  ? "#ffeaea"
                  : "#fff8e1",
            border:
              status === "success"
                ? "1px solid #34c759"
                : status === "error"
                  ? "1px solid #ff3b30"
                  : "1px solid #f0a500",
            fontSize: 13,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {JSON.stringify(resultado, null, 2)}
        </pre>
      )}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 13,
  color: "#6b7280",
  marginBottom: 4,
  fontWeight: 500,
};
